import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth'
import { firebase } from './firebase'
import { isFirebaseConfigured } from './env'

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in'

interface AuthValue {
  status: AuthStatus
  user: User | null
  /** The uid to read and write under. Each account gets its own subtree. */
  uid: string | null
  error: string | null
  signIn: () => Promise<void>
  signOutNow: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isFirebaseConfigured) return
    const { auth } = firebase()
    // Resolves the redirect fallback below; a no-op after a popup sign-in.
    void getRedirectResult(auth).catch((cause: unknown) => setError(describe(cause)))
    return onAuthStateChanged(auth, (next) => {
      setUser(next)
      setReady(true)
    })
  }, [])

  const value = useMemo<AuthValue>(() => {
    const status: AuthStatus = !ready ? 'loading' : user === null ? 'signed-out' : 'signed-in'

    return {
      status,
      user,
      uid: user?.uid ?? null,
      error,
      async signIn() {
        setError(null)
        const { auth } = firebase()
        const provider = new GoogleAuthProvider()
        provider.setCustomParameters({ prompt: 'select_account' })
        try {
          await signInWithPopup(auth, provider)
        } catch (cause: unknown) {
          // Installed PWAs and in-app browsers can refuse popups; fall back.
          if (isPopupUnavailable(cause)) {
            await signInWithRedirect(auth, provider)
            return
          }
          setError(describe(cause))
        }
      },
      async signOutNow() {
        await signOut(firebase().auth)
      },
    }
  }, [user, ready, error])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>')
  return value
}

const POPUP_CODES = new Set([
  'auth/popup-blocked',
  'auth/popup-closed-by-user',
  'auth/operation-not-supported-in-this-environment',
  'auth/cancelled-popup-request',
])

function isPopupUnavailable(cause: unknown): boolean {
  return typeof cause === 'object' && cause !== null && POPUP_CODES.has(String(Reflect.get(cause, 'code')))
}

function describe(cause: unknown): string {
  if (typeof cause === 'object' && cause !== null) {
    const code = Reflect.get(cause, 'code')
    if (typeof code === 'string') return code.replace('auth/', '').replaceAll('-', ' ')
    const message = Reflect.get(cause, 'message')
    if (typeof message === 'string') return message
  }
  return 'Sign-in failed.'
}
